/**
 * Geographic utility functions for coordinate and bounds calculations
 */

export interface Bounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

/**
 * Calculate corrected longitude offset accounting for latitude compression
 *
 * At any given latitude, 1° of longitude represents a shorter distance than
 * 1° of latitude due to Earth's spherical shape. This function converts a
 * latitude offset (in degrees) to the equivalent longitude offset at a specific
 * latitude using the cosine correction factor.
 *
 * Formula: longitudeOffset = latitudeOffset / cos(latitude)
 *
 * @param latitude - The latitude in degrees where the offset is measured
 * @param latitudeOffset - The offset in latitude degrees (e.g., 0.0015° ≈ 166m)
 * @returns The equivalent longitude offset in degrees at the given latitude
 *
 * @example
 * // At Poland's latitude (52°N), 0.0015° latitude ≈ 166m
 * // To get the same 166m distance in longitude:
 * const longitudeOffset = getLongitudeOffset(52, 0.0015);
 * // Returns ~0.00244° (because cos(52°) ≈ 0.615)
 *
 * @example
 * // At equator (0°), longitude and latitude offsets are equal:
 * const longitudeOffset = getLongitudeOffset(0, 0.0015);
 * // Returns 0.0015° (because cos(0°) = 1)
 */
export function getLongitudeOffset(
  latitude: number,
  latitudeOffset: number
): number {
  const latitudeRadians = (latitude * Math.PI) / 180;
  const correctionFactor = Math.cos(latitudeRadians);

  // Prevent division by zero at poles (latitude ±90°)
  if (Math.abs(correctionFactor) < 0.0001) {
    return latitudeOffset * 10000; // Arbitrary large value
  }

  return latitudeOffset / correctionFactor;
}
